/**
 * useVisibilityMode
 *
 * The single read path for the effective visibility view of the form currently
 * being rendered. Inside the PDF export portal it returns the export view
 * supplied by `PdfExportProvider` through `PdfExportContext`; everywhere else
 * the context override is absent and it returns the live on-screen view from
 * `formStore.state.visibilityMode` (resolved per role / form type on open,
 * SYD-2080).
 *
 * Reading visibility only through this hook keeps export/email rendering scoped
 * and structurally prevents an in-portal reader from accidentally rendering the
 * on-screen view (SYD-1948) — there is no `visibilityMode` on `useFormStore()`
 * to reach past the override. The snapshot tracks only `.visibilityMode`, so a
 * consumer re-renders solely on visibility changes.
 *
 * `.visibilityMode` is read before the `??`, not inside it: `??` short-circuits,
 * so with an export override present the snapshot result went unread — and a
 * `useSnapshot` whose result is never read subscribes to the whole proxy,
 * re-rendering every in-portal `LayoutUI` node on any form write.
 */

import { useSnapshot } from 'valtio';
import type { VisibilityTypeId } from '#types/System.types';
import { formStore } from '@/store';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';

export const useVisibilityMode = (): VisibilityTypeId => {
  const { visibilityMode: exportVisibilityMode } = usePdfExportContext();
  const snap = useSnapshot(formStore.state);
  const liveVisibilityMode = snap.visibilityMode;
  return exportVisibilityMode ?? liveVisibilityMode;
};
